// src/pages/workout/condition_guide/pain/video/detail.jsx
import { useEffect, useMemo } from "react";
import { useLoadingGate } from "../../../../../../../../hooks/useLoadingGate";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { resolveVidPath } from "../../../../../../../../utils/videoSource";
import AuthCard from "../../../../../../../../components/auth/AuthCard";
import BottomNav from "../../../../../../../../components/common/BottomNav";
import AutoMarquee from "../../../../../../../../components/common/AutoMarquee";
import SecureHlsPlayer from "../../../../../../../../components/video/SecureHlsPlayer";


const VIDEO_DETAIL = {
    "lb-1": {
        title: "허리 통증을 없애주는 허리운동",
        videoUrl: "",
        poster: "/assets/thumbs/lb1.jpg",
        guide: [
            "무리 없는 범위에서 10~12회 3세트 진행합니다.",
            "허리가 꺾이지 않도록 복부에 힘을 유지합니다.",
            "통증이 심해지면 즉시 중단하세요.",
        ],
    },
    "lb-2": {
        title: "허리협착증에 좋은 싱글레그",
        videoUrl: "",
        poster: "/assets/thumbs/lb2.jpg",
        guide: [
            "지지 다리 무릎을 살짝 굽히고, 중심을 유지합니다.",
            "동작 내내 골반이 틀어지지 않게 코어를 조입니다.",
        ],
    },

};

function splitPrettyTitle(raw) {
    if (!raw) return { head: "운동 영상", sub: "" };
    let head = raw.trim();
    let sub = "";

    const pm = head.match(/^(.*?)[\s]*\((.+)\)\s*$/);
    if (pm) {
        head = pm[1].trim();
        sub = pm[2].trim();
    } else {
        const hy = head.split(" - ");
        if (hy.length >= 2) {
            head = hy[0].trim();
            sub = hy.slice(1).join(" - ").trim();
        } else {
            const hy2 = head.split("-");
            if (hy2.length >= 2) {
                head = hy2[0].trim();
                sub = hy2.slice(1).join("-").trim();
            }
        }
    }
    if (!head) head = raw;
    return { head, sub };
}

const ensureArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

export default function PainVideoDetail() {
    const ready = useLoadingGate();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const { state: fromState = {} } = useLocation();

    // 쿼리
    const v = sp.get("v") || "";
    const cat = sp.get("cat") || "";
    const item = sp.get("item") || "";
    const titleFromQS = sp.get("title");

    // 리스트에서 넘겨준 state + VIDEO_DETAIL 둘 다 고려해서 데이터 구성
    const data = useMemo(() => {
        const fallback = VIDEO_DETAIL[v] || {};
        return {
            title: fromState.title ?? titleFromQS ?? fallback.title ?? "운동 영상",
            poster: fromState.poster ?? fallback.poster ?? "",
            guide: ensureArray(fromState.guide ?? fallback.guide),
            // videoUrl은 HLS가 없을 때만 사용 (예비용)
            videoUrl: fromState.videoUrl ?? fallback.videoUrl ?? "",
        };
    }, [fromState, titleFromQS, v]);

    // HLS 경로 (MSK와 동일)
    const vidPath = useMemo(() => resolveVidPath(v), [v]);
    const hasVideo = !!vidPath || !!data.videoUrl;

    // 제목 Marquee용 분리
    const pretty = useMemo(() => splitPrettyTitle(data.title), [data.title]);

    // 필수 파라미터 누락 체크 (MSK 느낌 그대로)
    const paramMissing = !v || !cat || !item;

    useEffect(() => {
        if (paramMissing) {
            const qs = new URLSearchParams({ item }).toString();
            navigate(`/workout/conditionguide/pain/video?${qs}`, { replace: true });
        }
    }, [paramMissing, item, cat, navigate]);

    if (paramMissing) return null;

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 md:px-8 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    title={
                        <AutoMarquee
                            className="text-center font-semibold break-keep"
                            reserveRight={14}
                            maxInline="36rem"
                        >
                            {pretty.sub ? `${pretty.head} (${pretty.sub})` : pretty.head}
                        </AutoMarquee>
                    }
                >
                    <div className="flex flex-col flex-1 pb-24 md:pb-28">
                        {/* 상단 영상/포스터 (MSK와 동일 패턴) */}
                        <div className="mt-2">
                            {hasVideo ? (
                                vidPath ? (
                                    // 🔹 MSK처럼 id → HLS 경로로 백엔드 영상 재생
                                    <SecureHlsPlayer
                                        vidPath={vidPath}
                                        poster={data.poster}
                                        autoPlay
                                    />
                                ) : (
                                    // 혹시 vidPath가 없고 videoUrl만 있는 특수 케이스용
                                    <video
                                        controls
                                        playsInline
                                        autoPlay
                                        muted
                                        controlsList="nodownload noplaybackrate"
                                        disablePictureInPicture
                                        className="w-full aspect-video rounded-2xl overflow-hidden bg-black cursor-pointer"
                                        src={data.videoUrl}
                                        poster={data.poster}
                                        onClick={(e) => {
                                            const vv = e.currentTarget;
                                            vv.paused ? vv.play() : vv.pause();
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === " " || e.key === "Enter") {
                                                e.preventDefault();
                                                const vv = e.currentTarget;
                                                vv.paused ? vv.play() : vv.pause();
                                            }
                                        }}
                                        tabIndex={0}
                                    />
                                )
                            ) : (
                                <div className="w-full aspect-video rounded-2xl border border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center">
                                    <p className="text-gray-600 font-medium">
                                        영상이 준비되지 않았습니다.
                                    </p>
                                    {data.poster ? (
                                        <img
                                            src={data.poster}
                                            alt="poster"
                                            className="mt-3 h-20 w-36 object-cover rounded-lg border border-gray-200"
                                        />
                                    ) : null}
                                </div>
                            )}
                        </div>

                        {/* 운동 가이드 */}
                        <section className="mt-4 md:mt-5 rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
                            <h3 className="text-lg md:text-xl font-bold mb-2 text-center">
                                운동 가이드
                            </h3>
                            <div className="border-b border-gray-200 mb-3"></div>
                            {data.guide.length ? (
                                <ol className="list-decimal ml-5 space-y-1.5 text-sm md:text-base text-gray-600">
                                    {data.guide.map((line, i) => (
                                        <li key={i} className="break-keep">
                                            {line}
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <p className="text-center text-gray-500 py-4">
                                    가이드 정보가 없습니다.
                                </p>
                            )}
                        </section>

                        {/* 하단 이전 버튼 */}
                        <div className="mt-6 flex justify-center">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="w-full border border-gray-200 rounded-lg py-3 text-gray-700 font-medium hover:bg-gray-50 transition"
                            >
                                이전으로
                            </button>
                        </div>
                    </div>
                </AuthCard>
            )}
            <BottomNav className="h-16 md:h-[72px]" />
        </main>
    );
}
